import { load as loadYaml } from 'js-yaml';

/**
 * リリースアセット情報
 */
export interface ReleaseAsset {
  /** ファイル名 */
  name: string;
  /** ダウンロードURL */
  url: string;
  /** ファイルサイズ（バイト） */
  size: number;
  /** SHA512ハッシュ */
  sha512?: string;
}

/**
 * プラットフォーム別リリース情報
 */
export interface PlatformRelease {
  /** バージョン */
  version: string;
  /** リリース日 */
  releaseDate?: string;
  /** インストーラー */
  installer: ReleaseAsset;
  /** 追加アセット（ZIPなど） */
  additionalAssets?: ReleaseAsset[];
}

/**
 * バージョン情報
 */
export interface VersionInfo {
  /** 最新バージョン */
  version: string;
  /** Windows向けリリース */
  windows?: PlatformRelease;
  /** macOS向けリリース */
  macos?: PlatformRelease;
  /** 最終更新日時 */
  lastUpdated: Date;
}

/**
 * electron-builderのlatest.yml形式
 */
interface LatestYml {
  version: string;
  releaseDate?: string;
  files: {
    url: string;
    sha512?: string;
    size: number;
  }[];
  path?: string;
  sha512?: string;
}

/**
 * バージョン情報サービス
 *
 * CloudFrontからlatest.yml / latest-mac.ymlを取得して
 * プラットフォーム別のバージョン情報を提供
 */
export class VersionInfoService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    // 末尾のスラッシュを除去
    this.baseUrl = baseUrl.replace(/\/+$/, '');
  }

  /**
   * YAMLをフェッチしてパース
   */
  private async fetchYaml(filename: string): Promise<LatestYml | null> {
    try {
      const response = await fetch(`${this.baseUrl}/${filename}`);
      if (!response.ok) {
        console.warn(`Failed to fetch ${filename}: ${response.status}`);
        return null;
      }

      const text = await response.text();
      // js-yaml v5 は空入力で例外を投げるためガード（旧挙動の undefined 返却に合わせ null を返す）
      if (!text.trim()) {
        return null;
      }
      return loadYaml(text) as LatestYml;
    } catch (error) {
      console.error(`Error fetching ${filename}:`, error);
      return null;
    }
  }

  /**
   * LatestYmlからPlatformReleaseに変換
   */
  private toPlatformRelease(data: LatestYml, platform: 'windows' | 'macos'): PlatformRelease {
    // プラットフォーム別に適切なインストーラーを選択
    // macOS: .dmg を優先、Windows: .exe を優先
    const preferredExtension = platform === 'macos' ? '.dmg' : '.exe';
    const installerFile =
      data.files.find((f) => f.url.toLowerCase().endsWith(preferredExtension)) ?? data.files[0];

    const installerName = installerFile?.url ?? data.path ?? '';
    const otherFiles = data.files.filter((f) => f.url !== installerName);

    const sha512Value = installerFile?.sha512 ?? data.sha512;
    const installer: ReleaseAsset = {
      name: installerName,
      url: `${this.baseUrl}/${installerName}`,
      size: installerFile?.size ?? 0,
    };
    if (sha512Value !== undefined) {
      installer.sha512 = sha512Value;
    }

    const additionalAssets: ReleaseAsset[] = otherFiles.map((file) => {
      const asset: ReleaseAsset = {
        name: file.url,
        url: `${this.baseUrl}/${file.url}`,
        size: file.size,
      };
      if (file.sha512 !== undefined) {
        asset.sha512 = file.sha512;
      }
      return asset;
    });

    const platformRelease: PlatformRelease = {
      version: data.version,
      installer,
      additionalAssets,
    };
    if (data.releaseDate) {
      platformRelease.releaseDate = data.releaseDate;
    }
    return platformRelease;
  }

  /**
   * 最新バージョン情報を取得
   */
  async getLatestVersion(): Promise<VersionInfo> {
    // 並行してWindows/macOS両方のYAMLを取得
    const [windowsYml, macosYml] = await Promise.all([
      this.fetchYaml('latest.yml'),
      this.fetchYaml('latest-mac.yml'),
    ]);

    // どちらかが取得できていれば、そのバージョンを使用
    const version = windowsYml?.version ?? macosYml?.version ?? 'Unknown';

    const result: VersionInfo = {
      version,
      lastUpdated: new Date(),
    };
    if (windowsYml) {
      result.windows = this.toPlatformRelease(windowsYml, 'windows');
    }
    if (macosYml) {
      result.macos = this.toPlatformRelease(macosYml, 'macos');
    }
    return result;
  }

  /**
   * 特定プラットフォームの最新情報を取得
   */
  async getPlatformRelease(platform: 'windows' | 'macos'): Promise<PlatformRelease | null> {
    const filename = platform === 'windows' ? 'latest.yml' : 'latest-mac.yml';
    const data = await this.fetchYaml(filename);

    if (!data) return null;
    return this.toPlatformRelease(data, platform);
  }

  /**
   * ユーザーのプラットフォームを検出
   */
  static detectPlatform(): 'windows' | 'macos' | 'unknown' {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes('win')) return 'windows';
    if (userAgent.includes('mac')) return 'macos';
    return 'unknown';
  }

  /**
   * ファイルサイズを人間が読める形式に変換
   */
  static formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }
}

// シングルトンインスタンス（URLは環境変数から取得）
let versionInfoServiceInstance: VersionInfoService | null = null;

export function getVersionInfoService(): VersionInfoService {
  if (!versionInfoServiceInstance) {
    const baseUrl = import.meta.env.VITE_UPDATE_SERVER_URL ?? '';
    if (!baseUrl) {
      console.warn('VITE_UPDATE_SERVER_URL is not set');
    }
    versionInfoServiceInstance = new VersionInfoService(baseUrl);
  }
  return versionInfoServiceInstance;
}
